import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import Logo from "@/components/brand/Logo";
import AnnouncementBar from "@/components/layout/AnnouncementBar";
import { useCart } from "@/context/CartContext";
import { STORE } from "@/lib/store";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const COLLECTIONS_NAV = [
  { slug: "metabolic-research", name: "Metabolic Research" },
  { slug: "cellular-longevity", name: "Cellular & Longevity" },
  { slug: "peptide-compounds", name: "Peptide Compounds" },
  { slug: "lipotropic-solutions", name: "Lipotropic Solutions" },
];

function CartBadge({ count }) {
  const reduce = useReducedMotion();
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.span
          key={count}
          initial={reduce ? false : { scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.4, opacity: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 24 }}
          className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8A96A] text-[#111C32] text-[10px] font-bold flex items-center justify-center"
          data-testid="cart-count"
        >
          {count}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-50">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:bg-[#1E2A44] focus:text-white focus:px-4 focus:py-2 focus:rounded-sm">
        Skip to content
      </a>
      <AnnouncementBar />
      <div
        className={`bg-white/95 backdrop-blur-md transition-shadow duration-500 ${
          scrolled ? "shadow-[0_10px_30px_rgba(30,42,68,0.08)]" : ""
        }`}
      >
        <div className="container-rnv flex items-center justify-between h-[76px] md:h-[92px]">
          <Logo imgClass="h-11 md:h-16 w-auto" className="shrink-0" />

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((n) => (
              <NavItem key={n.to} {...n} />
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <button
              aria-label="Search products"
              onClick={() => navigate("/search")}
              className="p-2 text-[#1E2A44] hover:text-[#C8A96A] transition-colors"
              data-testid="header-search-btn"
            >
              <Search size={20} strokeWidth={1.6} />
            </button>
            <a
              href={STORE.account}
              aria-label="Customer account"
              className="p-2 text-[#1E2A44] hover:text-[#C8A96A] transition-colors hidden sm:inline-flex"
              data-testid="header-account-btn"
            >
              <User size={20} strokeWidth={1.6} />
            </a>
            <Link
              to="/cart"
              aria-label={`Shopping cart, ${count} items`}
              className="relative p-2 text-[#1E2A44] hover:text-[#C8A96A] transition-colors"
              data-testid="header-cart-btn"
            >
              <ShoppingBag size={20} strokeWidth={1.6} />
              <CartBadge count={count} />
            </Link>
            <button
              className="p-2 text-[#1E2A44] lg:hidden"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-open"
            >
              <Menu size={24} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}

function NavItem({ label, to }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `text-sm link-underline py-2 transition-colors ${
          isActive ? "text-[#1E2A44] font-semibold" : "text-[#262E3D]"
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {label}
    </NavLink>
  );
}

function MobileMenu({ open, onClose }) {
  const links = NAV;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] bg-[#111C32] text-[#F7F4EF] lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between h-[76px] px-6">
            <Logo imgClass="h-12 w-auto" onNavy withLink={false} />
            <button aria-label="Close menu" onClick={onClose} className="p-2 text-[#F7F4EF]" data-testid="mobile-menu-close">
              <X size={28} />
            </button>
          </div>
          <nav className="px-6 pt-6 flex flex-col" aria-label="Mobile">
            {links.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i + 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="border-b border-white/10"
              >
                <Link
                  to={l.to}
                  onClick={onClose}
                  className="block py-5 font-serif text-3xl text-[#F7F4EF] hover:text-[#D8BE87] transition-colors"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
          </nav>
          <div className="px-6 pt-8 flex items-center gap-5">
            <a href={STORE.account} className="btn-gold !text-[#F7F4EF] !border-[#C8A96A]">Account</a>
            <Link to="/cart" onClick={onClose} className="btn-gold !text-[#F7F4EF] !border-[#C8A96A]">Cart</Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
